import React, { useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import PFContainerWrapper from 'components/common/select/BrowserContainer/individualPartners/PFContainerWrapper';
import { Grid, Checkbox, FormControlLabel, InputAdornment } from '@mui/material';
import { Autocomplete, Dialog, IconButton, TextField, Button } from '@totalsoft/rocket-ui';
import { DateTime } from '@ts-private/rocket-extensions';
import Switch from '@mui/material/Switch';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { GlobalRightsContext } from 'providers/GlobalRightsContext';
import memoize from 'lodash.memoize';
import { onCheckBoxChange, addPropertyPrefix } from 'utils/propertyChangeAdapters';
import Search from '@mui/icons-material/Search';
import { BoxActionsWrapper } from 'components/common/CommonStyles';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { RightsContext } from 'providers/RightsContext';
import { useTranslation } from 'react-i18next';

const addGeneralDataPrefix = memoize(addPropertyPrefix('generalData'));

export const OfferPartnerPFGeneralData = ({
  partnerGeneralData,
  agreesWithPersonalDataUse,
  idTypes,
  citizenships,
  relations,
  genderTypes,
  defaultOCRFileExtensions,
  validationInfo,
  checkMandatoryFields,
  config,
  onPropertyChange,
  onUniqueIdBlur,
  disabled: disabledFlag,
  fileInputRef,
  loadGDPR,
  onFileSelected,
  uploadClick,
  gdpr,
  isRestructuring,
  setIsPartnerFromModal,
  isAssociatedPartner,
  agreesWithPersonalDataUseReadOnly,
  isAssociatedDocumentPartner,
  enableOcrIntegration,
  disabledSearchByUniqueId
}) => {
  const { t } = useTranslation();
  const { masterPartnerSearch } = useContext(GlobalRightsContext);
  const {
    readOnlyFull,
    offerPartnerBasicInformationRead,
    offerPartnerExtendedInformationRead,
    offerPartnerRemarksRead,
    offerPartnerBasicInformationWrite,
    offerPartnerExtendedInformationWrite,
    offerPartnerRemarksWrite
  } = useContext(RightsContext);
  const [openGDPR, setOpenGDPR] = useState(false);
  const [isBrowserOpen, setIsBrowserOpen] = useState(false);
  const validExpireDate = current => {
    if (partnerGeneralData.identityCardReleaseDate) return current.isSameOrAfter(partnerGeneralData.identityCardReleaseDate);
    return true;
  };

  const validReleaseDate = current => {
    const currentTime = moment();
    if (partnerGeneralData.identityCardExpireDate)
      return current.isSameOrBefore(partnerGeneralData.identityCardExpireDate) && current.isSameOrBefore(currentTime);
    return current.isSameOrBefore(currentTime);
  };

  const handleGDPRPositive = () => {
    onPropertyChange('agreesWithPersonalDataUse', true);
    setOpenGDPR(false);
  };

  const handleGDPRClose = () => {
    setOpenGDPR(false);
  };

  const handleClientAgreesWithPersonalDataUse = memoize(value => {
    if (value) {
      loadGDPR();
      setOpenGDPR(value);
    } else {
      onPropertyChange('agreesWithPersonalDataUse', false);
    }
  });

  const CustomEndIcons = ({ onCustomClick, customDisabled }) => (
    <InputAdornment position='end'>
      <IconButton disabled={customDisabled} color='primary' size='tiny' variant='text' onClick={onCustomClick}>
        <Search />
      </IconButton>
    </InputAdornment>
  );

  const disabled = useMemo(
    () => (isAssociatedDocumentPartner ? disabledFlag : disabledFlag || !agreesWithPersonalDataUse),
    [disabledFlag, agreesWithPersonalDataUse, isAssociatedDocumentPartner]
  );

  return (
    <>
      {offerPartnerBasicInformationRead && !isAssociatedDocumentPartner && (
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                id={'offerPartnerFormCard_pf_agreesWithPersonalDataUse'}
                checked={agreesWithPersonalDataUse || false}
                onChange={onCheckBoxChange(handleClientAgreesWithPersonalDataUse)}
                color='primary'
                name='agreesWithPersonalDataUse'
                disabled={agreesWithPersonalDataUseReadOnly || readOnlyFull}
              />
            }
            label={t('OfferEdit.Client.AgreesWithPersonalDataUse')}
          />
        </Grid>
      )}
      <Grid container spacing={2} mb={4} alignItems='center'>
        <input
          type='file'
          accept={defaultOCRFileExtensions.toString()}
          style={{ display: 'none' }}
          ref={fileInputRef}
          onChange={onFileSelected}
        />
        {offerPartnerBasicInformationRead && (
          <>
            <Grid item xs={12} sm={6} md={4}>
              <PFContainerWrapper
                disabled={isAssociatedDocumentPartner || disabled || (!isAssociatedPartner && isRestructuring) || disabledSearchByUniqueId}
                label={t('OfferEdit.Client.PersonalIdentifier')}
                onTextFieldChange={addGeneralDataPrefix(onPropertyChange)('uniqueId')}
                onBlur={() => onUniqueIdBlur(partnerGeneralData.uniqueId)}
                value={partnerGeneralData.uniqueId}
                iconDisabled={
                  isAssociatedDocumentPartner ||
                  disabled ||
                  (!isAssociatedPartner && isRestructuring) ||
                  disabledSearchByUniqueId ||
                  !offerPartnerBasicInformationWrite
                }
                iconHidden={!masterPartnerSearch}
                error={!isRestructuring && validationInfo.uniqueId ? true : false}
                helperText={!isRestructuring && validationInfo.uniqueId}
                setIsBrowserOpen={setIsBrowserOpen}
                isBrowserOpen={isBrowserOpen}
                hasCustomIcons
                customEndIcons={
                  <CustomEndIcons
                    onCustomClick={() => {
                      setIsBrowserOpen(true);
                      setIsPartnerFromModal(true);
                    }}
                    customDisabled={
                      isAssociatedDocumentPartner || disabled || (!isAssociatedPartner && isRestructuring) || disabledSearchByUniqueId
                    }
                  />
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id={'offerPartnerFormCard_pf_firstName'}
                fullWidth
                required={checkMandatoryFields}
                value={partnerGeneralData.firstName || ''}
                error={validationInfo.firstName ? true : false}
                helperText={validationInfo.firstName}
                name='firstName'
                label={t('OfferEdit.Client.FirstName')}
                onChange={addGeneralDataPrefix(onPropertyChange)('firstName')}
                disabled={disabled || !offerPartnerBasicInformationWrite}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                id={'offerPartnerFormCard_pf_lastName'}
                fullWidth
                required={checkMandatoryFields}
                value={partnerGeneralData.lastName || ''}
                error={validationInfo.lastName ? true : false}
                helperText={validationInfo.lastName}
                name='lastName'
                label={t('OfferEdit.Client.LastName')}
                onChange={addGeneralDataPrefix(onPropertyChange)('lastName')}
                disabled={disabled || !offerPartnerBasicInformationWrite}
              />
            </Grid>
          </>
        )}
        {offerPartnerExtendedInformationRead && (
          <>
            <Grid item xs={12} sm={12} md={2}>
              <Autocomplete
                id={'offerPartnerFormCard_pf_personDocumentType'}
                fullWidth
                required={checkMandatoryFields}
                isClearable={false}
                value={partnerGeneralData.personDocumentTypeId}
                error={validationInfo.personDocumentTypeId ? true : false}
                helperText={validationInfo.personDocumentTypeId}
                onChange={addGeneralDataPrefix(onPropertyChange)('personDocumentTypeId')}
                label={t('OfferEdit.Client.IdType')}
                valueKey='id'
                name='personDocumentTypeId'
                options={idTypes}
                simpleValue={true}
                disabled={disabled || !offerPartnerExtendedInformationWrite}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={2}>
              <TextField
                id={'offerPartnerFormCard_pf_idSeriesAndNumber'}
                required={checkMandatoryFields}
                fullWidth
                value={partnerGeneralData.idSeriesAndNumber || ''}
                error={validationInfo.idSeriesAndNumber ? true : false}
                helperText={validationInfo.idSeriesAndNumber}
                name='idSeriesAndNumber'
                endAdornment={
                  enableOcrIntegration && (
                    <IconButton
                      id={'offerPartnerFormCard_pf_ocrBtn'}
                      color='primary'
                      variant='text'
                      size='tiny'
                      tooltip={t('OfferEdit.Client.OCR')}
                      disabled={disabled}
                      onClick={uploadClick}
                    >
                      <PhotoCameraIcon />
                    </IconButton>
                  )
                }
                label={t('OfferEdit.Client.IDSeriesAndNumber')}
                onChange={addGeneralDataPrefix(onPropertyChange)('idSeriesAndNumber')}
                disabled={disabled || !offerPartnerExtendedInformationWrite}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <TextField
                id={'offerPartnerFormCard_pf_idIssuedBy'}
                required={checkMandatoryFields}
                fullWidth
                value={partnerGeneralData.idIssuedBy || ''}
                error={validationInfo.idIssuedBy ? true : false}
                helperText={validationInfo.idIssuedBy}
                name='idIssuedBy'
                label={t('OfferEdit.Client.IDIssuedBy')}
                onChange={addGeneralDataPrefix(onPropertyChange)('idIssuedBy')}
                disabled={disabled || !offerPartnerExtendedInformationWrite}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={2}>
              <DateTime
                id={'offerPartnerFormCard_pf_identityCardReleaseDate'}
                fullWidth
                value={partnerGeneralData.identityCardReleaseDate}
                error={validationInfo.identityCardReleaseDate ? true : false}
                helperText={validationInfo.identityCardReleaseDate}
                label={t('OfferEdit.Client.IdentityCardReleaseDate')}
                onChange={addGeneralDataPrefix(onPropertyChange)('identityCardReleaseDate')}
                timeFormat={false}
                disabled={disabled || !offerPartnerExtendedInformationWrite}
                isValidDate={validReleaseDate}
                required={checkMandatoryFields}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={2}>
              <DateTime
                id={'offerPartnerFormCard_pf_identityCardExpireDate'}
                fullWidth
                value={partnerGeneralData.identityCardExpireDate}
                error={validationInfo.identityCardExpireDate ? true : false}
                helperText={validationInfo.identityCardExpireDate}
                label={t('OfferEdit.Client.IdentityCardExpireDate')}
                onChange={addGeneralDataPrefix(onPropertyChange)('identityCardExpireDate')}
                timeFormat={false}
                disabled={disabled || !offerPartnerExtendedInformationWrite}
                isValidDate={validExpireDate}
                required={checkMandatoryFields}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Autocomplete
                id={'offerPartnerFormCard_pf_citizenship'}
                fullWidth
                required={checkMandatoryFields}
                value={partnerGeneralData.citizenshipId}
                error={validationInfo.citizenshipId ? true : false}
                helperText={validationInfo.citizenshipId}
                onChange={addGeneralDataPrefix(onPropertyChange)('citizenshipId')}
                label={t('OfferEdit.Client.Citizenship')}
                valueKey='id'
                name='citizenship'
                options={citizenships}
                simpleValue={true}
                disabled={disabled || !offerPartnerExtendedInformationWrite}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Autocomplete
                id={'offerPartnerFormCard_pf_nationality'}
                fullWidth
                required={checkMandatoryFields}
                value={partnerGeneralData.nationalityId}
                error={validationInfo.nationalityId ? true : false}
                helperText={validationInfo.nationalityId}
                onChange={addGeneralDataPrefix(onPropertyChange)('nationalityId')}
                label={t('OfferEdit.Client.Nationality')}
                valueKey='id'
                name='nationality'
                options={citizenships}
                simpleValue={true}
                disabled={disabled || !offerPartnerExtendedInformationWrite}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={1}>
              <FormControlLabel
                control={
                  <Switch
                    id={'offerPartnerFormCard_pf_isExternal'}
                    checked={partnerGeneralData.isExternal || false}
                    onChange={onCheckBoxChange(addGeneralDataPrefix(onPropertyChange)('isExternal'))}
                    name='isExternal'
                    disabled={disabled || !offerPartnerExtendedInformationWrite}
                  />
                }
                label={t('OfferEdit.Client.External')}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <TextField
                id={'offerPartnerFormCard_pf_birthPlace'}
                fullWidth
                value={partnerGeneralData.birthPlace || ''}
                error={validationInfo.birthPlace ? true : false}
                label={t('OfferEdit.Client.BirthPlace')}
                onChange={addGeneralDataPrefix(onPropertyChange)('birthPlace')}
                disabled={disabled || !offerPartnerExtendedInformationWrite}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <DateTime
                id={'offerPartnerFormCard_pf_birthDate'}
                fullWidth
                value={partnerGeneralData.birthDate}
                error={validationInfo.birthDate ? true : false}
                helperText={validationInfo.birthDate}
                label={t('OfferEdit.Client.BirthDate')}
                onChange={addGeneralDataPrefix(onPropertyChange)('birthDate')}
                timeFormat={false}
                disabled={disabled || !offerPartnerExtendedInformationWrite}
                required={checkMandatoryFields}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={1}>
              <FormControlLabel
                control={
                  <Switch
                    id={'offerPartnerFormCard_pf_pep'}
                    checked={partnerGeneralData.pep || false}
                    onChange={onCheckBoxChange(addGeneralDataPrefix(onPropertyChange)('pep'))}
                    name='pep'
                    disabled={disabled || !offerPartnerExtendedInformationWrite}
                  />
                }
                label={t('OfferEdit.Client.PEP')}
              />
            </Grid>
          </>
        )}
        {offerPartnerBasicInformationRead && (
          <>
            <Grid item xs={12} sm={12} md={4}>
              <Autocomplete
                id={'offerPartnerFormCard_pf_relation'}
                fullWidth
                required={checkMandatoryFields}
                isClearable={false}
                value={partnerGeneralData.relationId || config.defaultRelationId || ' '}
                error={Boolean(validationInfo.relationId)}
                helperText={validationInfo.relationId}
                onChange={addGeneralDataPrefix(onPropertyChange)('relationId')}
                label={t('OfferEdit.Client.Relation')}
                valueKey='id'
                name='relation'
                options={relations}
                simpleValue={true}
                disabled={disabled || !offerPartnerBasicInformationWrite}
                placeholder={t('OfferEdit.Client.NoRelation')}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={4}>
              <Autocomplete
                id={'offerPartnerFormCard_pf_gender'}
                fullWidth
                required={checkMandatoryFields}
                value={partnerGeneralData.genderId || ' '}
                error={Boolean(validationInfo.genderId)}
                helperText={validationInfo.genderId}
                onChange={addGeneralDataPrefix(onPropertyChange)('genderId')}
                label={t('OfferEdit.Client.Sex')}
                valueKey='id'
                labelKey='name'
                name='gender'
                options={genderTypes}
                simpleValue={true}
                disabled={disabled || !offerPartnerBasicInformationWrite}
              />
            </Grid>
          </>
        )}
        {offerPartnerRemarksRead && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              id={'offerPartnerFormCard_remarks'}
              label={t('OfferEdit.Client.Remarks')}
              value={partnerGeneralData.remark || ''}
              onChange={addGeneralDataPrefix(onPropertyChange)('remark')}
              multiline
              inputProps={{ maxLength: 255 }}
              variant='outlined'
              disabled={disabled || !offerPartnerRemarksWrite}
            />
          </Grid>
        )}
      </Grid>
      <Dialog
        open={openGDPR}
        onClose={handleGDPRClose}
        aria-labelledby='gdpr-title'
        aria-describedby='gdpr-description'
        title={t('OfferEdit.Client.GDPR.Title')}
        content={!gdpr.isLoaded ? <WrappedFakeText /> : <div dangerouslySetInnerHTML={{ __html: gdpr.text }} />}
        actions={
          <BoxActionsWrapper forDialog>
            <Button id={'offerPartnerFormCard_pf_gdprAcceptBtn'} disabled={!gdpr.isLoaded} autoFocus onClick={handleGDPRPositive}>
              {t('OfferEdit.Client.GDPR.AcceptButton')}
            </Button>
            <Button id={'offerPartnerFormCard_pf_gdprRejectBtn'} disabled={!gdpr.isLoaded} onClick={handleGDPRClose}>
              {t('OfferEdit.Client.GDPR.RejectButton')}
            </Button>
          </BoxActionsWrapper>
        }
      />
    </>
  );
};

OfferPartnerPFGeneralData.propTypes = {
  partnerGeneralData: PropTypes.object.isRequired,
  validationInfo: PropTypes.object.isRequired,
  checkMandatoryFields: PropTypes.bool.isRequired,
  agreesWithPersonalDataUse: PropTypes.bool.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onUniqueIdBlur: PropTypes.func.isRequired,
  citizenships: PropTypes.array.isRequired,
  idTypes: PropTypes.array.isRequired,
  relations: PropTypes.array.isRequired,
  genderTypes: PropTypes.array.isRequired,
  defaultOCRFileExtensions: PropTypes.array.isRequired,
  config: PropTypes.object.isRequired,
  onFileSelected: PropTypes.func.isRequired,
  uploadClick: PropTypes.func.isRequired,
  fileInputRef: PropTypes.object,
  disabled: PropTypes.bool.isRequired,
  loadGDPR: PropTypes.func.isRequired,
  gdpr: PropTypes.object.isRequired,
  loadCitiesWithCounty: PropTypes.func.isRequired,
  isRestructuring: PropTypes.bool.isRequired,
  setIsPartnerFromModal: PropTypes.func.isRequired,
  isAssociatedPartner: PropTypes.bool,
  agreesWithPersonalDataUseReadOnly: PropTypes.bool.isRequired,
  onCustomClick: PropTypes.func,
  customDisabled: PropTypes.bool,
  isAssociatedDocumentPartner: PropTypes.bool,
  enableOcrIntegration: PropTypes.bool,
  disabledSearchByUniqueId: PropTypes.bool
};

export default OfferPartnerPFGeneralData;
